import React, { useCallback, useEffect, useState } from 'react';

import { BugoBasicButton } from '@service/bugo/components/BugoBasicButton';
import {
  LoginPhoneChallenge,
  isLoginPhoneChallenge,
} from '@shared/api/auth/loginPhoneVerification.controller';
import { LoginHelpPreset } from '@shared/api/enquiry/enquiry.interface';
import { AccountInput } from '@shared/components/AccountInput';
import { LoginFailureCard } from '@shared/components/LoginFailureCard';
import { LoginHelpForm } from '@shared/components/LoginHelpForm';
import { LoginPhoneVerification } from '@shared/components/LoginPhoneVerification';
import { CheckBox } from '@shared/containers';
import { useAuth } from '@shared/state/hooks/useAuth';
import {
  clearLoginDeviceLock,
  getActiveLoginDeviceLock,
} from '@shared/utils/loginDevice.util';
import { LoginFailure, parseLoginFailure } from '@shared/utils/loginError.util';
import { resolveLoginRedirectUrl } from '@shared/utils/loginRedirect.util';
import {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  redirectUrl: string;
  /**
   * 어느 화면의 로그인 폼인가.
   * - `console`(기본): 운영 콘솔(관리자·상조·판매자·답례품·배송관리자). 기기 헤더를 실어
   *   서버가 실패 횟수 잠금을 걸고, 잠김 캐시·카운트다운을 그린다.
   * - `customer`: 고객 화면(화환 스토어 로그인 등). **고객은 어떤 경우에도 잠기면 안 되므로**
   *   기기 헤더를 빼고 보내고(서버가 잠금을 건너뜀), 같은 브라우저의 콘솔 잠김 캐시도 읽지 않는다.
   */
  surface?: 'console' | 'customer';
}

const LoginForm: React.FC<LoginFormProps> = ({ redirectUrl, surface = 'console' }) => {
  const customerSurface = surface === 'customer';
  const { register, handleSubmit, control, setValue, setFocus } = useForm();
  const { loginByUsernamePassword, completeLoginWithToken } = useAuth();
  const navigate = useNavigate();

  //* 운영 계정(Admin) 2단계 인증 — 비밀번호는 맞았고 토큰은 아직 없는 상태
  const [phoneChallenge, setPhoneChallenge] = useState<LoginPhoneChallenge | null>(null);

  //* 실패 상태는 토스트가 아니라 폼 아래 카드로 — 몇 번 남았는지가 남아 있어야 전화로 이어진다.
  //* 새로고침해도 잠김이면 잠긴 화면으로 열린다 (캐시. 진실은 서버)
  const [failure, setFailure] = useState<LoginFailure | null>(() => {
    if (customerSurface) return null; //* 고객 화면은 콘솔 잠김 캐시와 무관
    const lock = getActiveLoginDeviceLock();
    return lock ? { kind: 'locked', lockedUntil: lock.lockedUntil } : null;
  });
  const locked = failure?.kind === 'locked';

  //* 잠금이 풀리면(카운트다운 종료·[다시 시도]) 비밀번호 칸으로 바로 보낸다
  useEffect(() => {
    if (!locked && failure) setFocus('password');
  }, [locked, failure, setFocus]);

  const reopenForm = useCallback(() => {
    clearLoginDeviceLock();
    setFailure(null);
  }, []);

  //* 로그인 문의(Slack) 폼. null 이면 닫힘, 값은 미리 골라 둘 종류
  const [helpPreset, setHelpPreset] = useState<LoginHelpPreset | null>(null);
  const openHelp = useCallback(() => {
    //* 실패 상황에 맞춰 종류를 미리 고른다 — 한 번 눌러 보낼 수 있게
    if (failure?.kind === 'locked') setHelpPreset(LoginHelpPreset.DeviceLocked);
    else if (failure?.kind === 'phone-not-registered')
      setHelpPreset(LoginHelpPreset.PhoneChanged);
    else setHelpPreset(LoginHelpPreset.ForgotPassword);
  }, [failure]);
  const typedUsername = useWatch({ control, name: 'username', defaultValue: '' });

  const saveCheckBoxHandler = (checkState: boolean) => {
    setValue('saveLoginDataChecked', checkState);
  };

  const saveLoginDataChecked = useWatch({
    control,
    name: 'saveLoginDataChecked',
    defaultValue: false,
  });

  useEffect(() => {
    const checked = localStorage.getItem('saveLoginDataChecked');
    if (checked === 'true') {
      const username = localStorage.getItem('username');
      const password = localStorage.getItem('password');

      setValue('saveLoginDataChecked', true);
      setValue('username', username);
      setValue('password', password);
    }
  }, [setValue]);

  const onSubmit: SubmitHandler<FieldValues> = async (data, e) => {
    const { username, password, saveLoginDataChecked } = data;

    if (saveLoginDataChecked) {
      localStorage.setItem('saveLoginDataChecked', saveLoginDataChecked);
      localStorage.setItem('username', username);
      localStorage.setItem('password', password);
    } else {
      localStorage.removeItem('saveLoginDataChecked');
      localStorage.removeItem('username');
      localStorage.removeItem('password');
    }

    //* 잠긴 동안은 서버를 두드리지 않는다. [다시 시도] 로 폼을 연 뒤에만 보낸다
    if (locked) return;

    try {
      const result = await loginByUsernamePassword(
        {
          username: username,
          password: password,
        },
        { customerSurface },
      );
      if (!customerSurface) clearLoginDeviceLock();
      setFailure(null);

      //* 토큰 대신 challenge가 왔으면 아직 로그인 전 — 휴대폰 인증 화면으로
      if (isLoginPhoneChallenge(result)) {
        setPhoneChallenge(result);
        return;
      }

      //* GiftAgent 단독 계정은 답례품 판매자 포털로 바로 보낸다
      navigate(resolveLoginRedirectUrl(result, redirectUrl));
    } catch (error) {
      if ((error as Error)?.message === 'BUGO_BRAND_ACCESS_DENIED') return;
      //* 권한 없음·네트워크 오류 등('other')도 카드 한 줄로 — 토스트와 같은 문구를 겹쳐 띄우지 않는다
      setFailure(parseLoginFailure(error));
    }
  };

  const onError: SubmitErrorHandler<FieldValues> = (errors, e) => {
    console.log(errors, e);
  };

  const handlePhoneVerified = useCallback(
    async (token: string) => {
      try {
        await completeLoginWithToken(token);
        setPhoneChallenge(null);
        navigate(resolveLoginRedirectUrl(token, redirectUrl));
      } catch (error) {
        //* 접속 브랜드 불일치 등 — 안내는 useAuth가 띄운다. 처음으로 되돌린다
        setPhoneChallenge(null);
      }
    },
    [completeLoginWithToken, navigate, redirectUrl],
  );

  if (helpPreset) {
    return (
      <LoginHelpForm
        initialPreset={helpPreset}
        username={typeof typedUsername === 'string' ? typedUsername : undefined}
        customerSurface={customerSurface}
        onBack={() => setHelpPreset(null)}
      />
    );
  }

  if (phoneChallenge) {
    return (
      <LoginPhoneVerification
        challenge={phoneChallenge}
        onVerified={handlePhoneVerified}
        onCancel={() => setPhoneChallenge(null)}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      {/* Id */}
      <AccountInput
        register={register}
        registerName={'username'}
        type="text"
        text="아이디"
        placeholder="아이디를 입력해주세요"
        className="text-15"
        disabled={locked}
      />
      {/* Password */}
      <AccountInput
        register={register}
        registerName={'password'}
        type="password"
        passwordToggle
        text="비밀번호"
        placeholder="비밀번호를 입력해주세요"
        className="text-15"
        disabled={locked}
      />
      {failure && (
        <div className="mt-3">
          <LoginFailureCard
            failure={failure}
            onRetry={reopenForm}
            onLockExpired={reopenForm}
            onHelp={openHelp}
          />
        </div>
      )}
      {/* ButtonSet */}
      {/* OptionButtonSet */}
      <div className="my-4 flex justify-between">
        {/* PersistCheckBox */}
        <label className="flex justify-start">
          <CheckBox
            checked={saveLoginDataChecked}
            onCheckedChange={saveCheckBoxHandler}
          />
          <div className="ml-2 h-full">로그인 정보 저장</div>
        </label>
        {/* FindIdButton */}
        {/* <div className="h-full">
          <Button className="h-5">아이디/비밀번호 찾기</Button>
        </div> */}
      </div>
      {/* SubmitButton */}
      <BugoBasicButton
        type="submit"
        className="rounded-lg theme-bg-main disabled:opacity-40"
        text="로그인"
        disabled={locked}
      />
      {/* 실패 카드가 이미 [문의 남기기] 를 보여주면 아래 줄은 중복이라 뺀다 */}
      {!failure && (
        <p className="mt-3 text-center text-13 text-gray-500">
          로그인이 어려우신가요?{' '}
          <button
            type="button"
            onClick={openHelp}
            className="font-semibold underline underline-offset-2 theme-text-main"
          >
            문의 남기기
          </button>
        </p>
      )}
    </form>
  );
};

export { LoginForm };
